import { randomInt } from './rng.js';
import { SHIP_SIZES, shipCount } from './ruleset.js';
/** Klucz pola w mapie zajętości. Zależy od rozmiaru planszy, więc zawsze podajemy `size`. */
export function cellKey(x, y, boardSize) {
    return y * boardSize + x;
}
export function inBounds(x, y, boardSize) {
    return x >= 0 && y >= 0 && x < boardSize && y < boardSize;
}
/** Pola zajmowane przez statek, od dziobu do rufy. */
export function cellsOf(ship) {
    const cells = [];
    for (let i = 0; i < ship.size; i++) {
        cells.push(ship.dir === 'h' ? { x: ship.x + i, y: ship.y } : { x: ship.x, y: ship.y + i });
    }
    return cells;
}
/** Ośmiu sąsiadów pola, przycięte do planszy. */
export function neighbours8(c, boardSize) {
    const out = [];
    for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0)
                continue;
            const x = c.x + dx;
            const y = c.y + dy;
            if (inBounds(x, y, boardSize))
                out.push({ x, y });
        }
    }
    return out;
}
/**
 * Aureola statku: pola stykające się z nim (również po skosie), ale do niego nienależące.
 * To dokładnie te pola, na których nie może stanąć inny statek — i te, które
 * odkrywają się automatycznie po zatopieniu, gdy `autoRevealHaloOnSunk` jest włączone.
 */
export function haloOf(ship, boardSize) {
    const own = new Set(cellsOf(ship).map((c) => cellKey(c.x, c.y, boardSize)));
    const seen = new Set();
    const out = [];
    for (const cell of cellsOf(ship)) {
        for (const n of neighbours8(cell, boardSize)) {
            const key = cellKey(n.x, n.y, boardSize);
            if (own.has(key) || seen.has(key))
                continue;
            seen.add(key);
            out.push(n);
        }
    }
    return out;
}
/** Mapa pole → id statku. Zwraca `null`, jeśli statki nachodzą na siebie lub wychodzą poza planszę. */
export function occupancy(fleet, boardSize) {
    const owner = new Map();
    for (const ship of fleet) {
        for (const c of cellsOf(ship)) {
            if (!inBounds(c.x, c.y, boardSize))
                return null;
            const key = cellKey(c.x, c.y, boardSize);
            if (owner.has(key))
                return null;
            owner.set(key, ship.id);
        }
    }
    return owner;
}
/** Czy skład floty zgadza się z zasadami (np. dokładnie 4 jednomasztowce). */
export function matchesRoster(fleet, roster) {
    if (fleet.length !== shipCount(roster))
        return false;
    const counts = new Map();
    for (const ship of fleet)
        counts.set(ship.size, (counts.get(ship.size) ?? 0) + 1);
    return SHIP_SIZES.every((size) => (counts.get(size) ?? 0) === roster[size]);
}
/**
 * Pełna walidacja floty. To jest funkcja, która decyduje o legalności rozstawienia —
 * wywoływana w UI (podgląd na żywo) i na serwerze (autorytet). Jedno źródło prawdy.
 *
 * Reguła niestykania się: dla dowolnych dwóch RÓŻNYCH statków A i B oraz pól a ∈ A, b ∈ B
 * musi zachodzić `max(|a.x − b.x|, |a.y − b.y|) ≥ 2`.
 */
export function validateFleet(fleet, ruleset) {
    const errors = [];
    const { boardSize } = ruleset;
    const ids = new Set();
    for (const ship of fleet) {
        if (ids.has(ship.id))
            errors.push({ code: 'DUPLICATE_ID', shipId: ship.id });
        ids.add(ship.id);
        if (!SHIP_SIZES.includes(ship.size))
            errors.push({ code: 'INVALID_SIZE', shipId: ship.id });
    }
    if (!matchesRoster(fleet, ruleset.roster))
        errors.push({ code: 'ROSTER_MISMATCH' });
    const owner = new Map();
    for (const ship of fleet) {
        for (const c of cellsOf(ship)) {
            if (!inBounds(c.x, c.y, boardSize)) {
                errors.push({ code: 'OUT_OF_BOUNDS', shipId: ship.id });
                continue;
            }
            const key = cellKey(c.x, c.y, boardSize);
            if (owner.has(key))
                errors.push({ code: 'OVERLAP', shipId: ship.id });
            else
                owner.set(key, ship.id);
        }
    }
    for (const ship of fleet) {
        let touching = false;
        for (const cell of cellsOf(ship)) {
            for (const n of neighbours8(cell, boardSize)) {
                const other = owner.get(cellKey(n.x, n.y, boardSize));
                if (other !== undefined && other !== ship.id)
                    touching = true;
            }
        }
        if (touching)
            errors.push({ code: 'TOUCHING', shipId: ship.id });
    }
    return errors.length === 0 ? { ok: true } : { ok: false, errors };
}
export function isFleetLegal(fleet, ruleset) {
    return validateFleet(fleet, ruleset).ok;
}
/**
 * Czy `candidate` da się dostawić do już rozstawionych statków.
 * Używane przez edytor do kolorowania "ducha" statku pod kursorem, więc musi być tanie.
 */
export function canPlace(placed, candidate, boardSize) {
    const cells = cellsOf(candidate);
    if (cells.some((c) => !inBounds(c.x, c.y, boardSize)))
        return false;
    const blocked = new Set();
    for (const ship of placed) {
        if (ship.id === candidate.id)
            continue;
        for (const c of cellsOf(ship))
            blocked.add(cellKey(c.x, c.y, boardSize));
        for (const c of haloOf(ship, boardSize))
            blocked.add(cellKey(c.x, c.y, boardSize));
    }
    return cells.every((c) => !blocked.has(cellKey(c.x, c.y, boardSize)));
}
/** Wszystkie pola zablokowane przez już rozstawione statki (kadłuby + aureole). */
export function blockedCells(placed, boardSize) {
    const blocked = new Set();
    for (const ship of placed) {
        for (const c of cellsOf(ship))
            blocked.add(cellKey(c.x, c.y, boardSize));
        for (const c of haloOf(ship, boardSize))
            blocked.add(cellKey(c.x, c.y, boardSize));
    }
    return blocked;
}
const MAX_TRIES_PER_SHIP = 200;
const MAX_RESTARTS = 100;
/**
 * Losowe, zawsze legalne rozstawienie floty.
 *
 * Strategia: od najdłuższego statku (najtrudniej go wcisnąć), losowa pozycja z limitem prób,
 * a po serii niepowodzeń restart całej planszy. Przy klasycznej flocie na 10×10 zajęte jest
 * ~20% pól, więc restart praktycznie nie występuje — limity są zabezpieczeniem dla
 * niestandardowych zestawów zasad (np. gęsta flota na małej planszy).
 */
export function randomFleet(rng, ruleset) {
    const { boardSize, roster } = ruleset;
    const sizes = [];
    for (const size of [...SHIP_SIZES].sort((a, b) => b - a)) {
        for (let i = 0; i < roster[size]; i++)
            sizes.push(size);
    }
    for (let restart = 0; restart < MAX_RESTARTS; restart++) {
        const fleet = [];
        let failed = false;
        for (const [index, size] of sizes.entries()) {
            let placedShip = null;
            for (let attempt = 0; attempt < MAX_TRIES_PER_SHIP; attempt++) {
                const dir = rng() < 0.5 ? 'h' : 'v';
                const span = boardSize - size + 1;
                const x = dir === 'h' ? randomInt(rng, span) : randomInt(rng, boardSize);
                const y = dir === 'h' ? randomInt(rng, boardSize) : randomInt(rng, span);
                const candidate = { id: index, size, x, y, dir };
                if (canPlace(fleet, candidate, boardSize)) {
                    placedShip = candidate;
                    break;
                }
            }
            if (!placedShip) {
                failed = true;
                break;
            }
            fleet.push(placedShip);
        }
        if (!failed)
            return fleet;
    }
    throw new Error('randomFleet: nie udało się rozstawić floty dla podanych zasad');
}
//# sourceMappingURL=placement.js.map